import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button
} from '@mui/material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import MarkdownRenderer from '../common/MarkdownRenderer';
import SectionDetailsRenderer from '../common/SectionDetailsRenderer';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useExplain from '../../hooks/useExplain';
import useFeatureVersions from '../../hooks/useFeatureVersions';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
// Import full screen functionality
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import LoadingBox from '../atoms/LoadingBox';
import { capitalizeFirst } from '../../utils/textTransformations';
import conversationService from '../../services/conversationService';
import AddSectionModal from '../atoms/AddSectionModal';
import { analyzeContentFields, generateContentFromFields } from '../../utils/contentFieldParser';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import useParsedSections from '../../hooks/useParsedSections';


const SplitViewConversation = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [downloadingFeatureId, setDownloadingFeatureId] = useState(null);
  const [isDownloadAvailable, setIsDownloadAvailable] = useState(false);
  const { showNotification } = useNotification();
  const featurePanelRef = useRef(null);

  // Helper function to format feature titles to sentence case
  const formatFeatureTitleToSentenceCase = (title) => {
    if (!title) return title;

    // Handle F001: pattern - capitalize first word after colon
    const colonMatch = title.match(/^(F\d+:\s*)(.+)$/);
    if (colonMatch) {
      const prefix = colonMatch[1];
      const content = colonMatch[2];
      // Use capitalizeFirst utility for the content part
      return prefix + capitalizeFirst(content);
    }

    // For other titles, use capitalizeFirst utility
    return capitalizeFirst(title);
  };

  // Track if we're doing a local update (passed to CRUD hooks)
  const isLocalUpdateRef = useRef(false);

  // Use version management for features
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useFeatureVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedFeatures,
    setSections: setParsedFeatures,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef: featureContentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // Refs for text selection per section (for inline comment popup)
  const sectionContentRefs = useRef({});
  const getSectionRef = useCallback((sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  }, []);

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'features',
  });

  // State for managing expanded/visible features
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [visibleFeatures, setVisibleFeatures] = useState(10);

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedFeatures, setExpandedFeatures);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible features when switching versions
  useEffect(() => {
    if (currentVersion) {
      // Only reset visible features when actually switching versions
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleFeatures(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);


  // Check if this is a feature conversation
  // For 'project' type: uses 'features' task
  // For 'new-feature' type: uses 'feature-info' task
  // For 'change-request' type: uses 'change-request-info' task
  const isFeatureConversation = conversation?.taskId === 'features' ||
    conversation?.taskId === 'feature-info' ||
    conversation?.taskId === 'change-request-info' ||
    (conversation?.messages?.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('## F0') || msg.content?.includes('**Priority:**'))
    ));

  const showFeaturePanel = parsedFeatures.length > 0;

  const handleFeatureToggle = (featureId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleFeatures(prev => Math.min(prev + 10, parsedFeatures.length));
  };

  const handleShowAll = () => {
    setVisibleFeatures(parsedFeatures.length);
  };

  // Use CRUD operations hook
  const {
    editingSection: editingFeature,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit: handleStartEditOriginal,
    handleCancelEdit,
    handleSaveEdit: handleSaveEditOriginal,
    handleDeleteSection: handleDeleteFeatureClick,
    handleDeleteSectionConfirmed: handleDeleteConfirmGeneric,
    handleExplainSection: handleExplainFeatureOriginal,
    handleCopySection: handleCopyFeatureOriginal
  } = useSectionCRUD(conversation, parsedFeatures, setParsedFeatures, setExpandedFeatures, featureContentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, onConversationUpdate, markLocalEdit);

  // Custom delete handler that properly reconstructs feature markdown with categories
  const handleDeleteConfirm = async () => {
    const feature = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!feature) return;

    try {
      // Remove the feature from the features array (keep original IDs, no renumbering)
      const updatedFeatures = parsedFeatures.filter(f => f.id !== feature.id);

      // Recalculate isFirstInCategory for remaining features
      const seenCategories = new Set();
      updatedFeatures.forEach(f => {
        if (f.category && !seenCategories.has(f.category)) {
          f.isFirstInCategory = true;
          seenCategories.add(f.category);
        } else {
          f.isFirstInCategory = false;
        }
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedFeatures(updatedFeatures);

      // Remove expanded state for the deleted feature
      setExpandedFeatures(prev => {
        const newExpanded = { ...prev };
        delete newExpanded[feature.id];
        return newExpanded;
      });

      // Save sections as JSON — backend reconstructs markdown and syncs to conversation
      const messageTimestamp = currentVersion?.timestamp;
      let updatedContent = '';

      if (messageTimestamp && conversation._id) {
        try {
          const result = await conversationService.updateParsedSections(
            conversation._id,
            messageTimestamp,
            updatedFeatures
          );
          updatedContent = result?.data?.markdown || '';
        } catch (err) {
          console.warn('JSON save failed on delete, falling back:', err);
        }
      }

      featureContentRef.current = updatedContent.trim();

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          features: updatedFeatures,
          parsedContent: updatedFeatures
        });
      }

      // Update LHS
      if (onConversationUpdate && conversation._id) {
        const updatedMessages = [...conversation.messages];
        if (currentVersion?.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx]?.role === 'assistant') {
            updatedMessages[idx] = {
              ...updatedMessages[idx],
              content: updatedContent.trim(),
              parsed_content: {
                sections: updatedFeatures,
                metadata: { total_sections: updatedFeatures.length, task_id: conversation?.taskId || conversation?.task_id }
              }
            };
          }
        }
        await onConversationUpdate(conversation._id, updatedMessages);
      }

      showNotification({
        message: `"${feature.title}" deleted successfully`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error deleting feature:', error);
      showNotification({
        message: `Failed to delete "${feature.title}"`,
        type: 'error'
      });
    }
  };

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport: handleExportFeatures,
    getExportContent,
    handleCopy: handleCopyFeatures,
    handleRefresh: handleRefreshFeatures
  } = useContentActions(featureContentRef, 'Features', projectId, conversation?._id);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();
  // Initialize validation state for features with version-specific IDs
  const initialValidationState = parsedFeatures.reduce((acc, feature) => {
    // Create version-specific validation ID
    const versionSpecificId = `${feature.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedFeatures,
    handleValidateItem: handleValidateFeatureBase,
    isLoading: isLoadingValidations,
    setValidatedItems: setValidatedFeatures,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'features',
    initialState: initialValidationState
  });

  // Wrapper function to handle version-specific validation
  const handleValidateFeature = async (featureId) => {
    // Create version-specific ID
    const versionSpecificId = `${featureId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateFeatureBase(versionSpecificId);
  };

  // Create wrapper functions to maintain compatibility with existing code
  const handleStartEdit = (featureId, feature) => {
    // Extract content for editing, prioritizing rawContent
    let contentToEdit = '';
    if (feature.rawContent) {
      // Remove the title line (## F001: Title) from the content
      contentToEdit = feature.rawContent.replace(/^(?:###?|##)\s+F\d+:[^\n]*\n+/, '');
    } else if (feature.content) {
      // Also try to remove title from regular content
      contentToEdit = feature.content.replace(/^(?:###?|##)\s+F\d+:[^\n]*\n+/, '');
    }
    // Pass the title for editing as-is
    handleStartEditOriginal(featureId, contentToEdit, formatFeatureTitleToSentenceCase(feature.title) || '');
  };

  const handleSaveEdit = (featureId, featureIndex) => {
    return handleSaveEditOriginal(featureId);
  };

  const handleCopyFeature = (feature) => {
    return handleCopyFeatureOriginal(feature);
  };


  const handleExplainFeature = (feature) => {
    requestExplanation(feature);
  };

  const handleImproveFeature = (feature) => {
    handleImproveClick(feature);
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        const updatedFeatures = parsedFeatures.filter(f => f.id !== improveSection.id);
        markLocalEdit();
        isLocalUpdateRef.current = true;
        setParsedFeatures(updatedFeatures);

        // Save to backend
        const messageTimestamp = currentVersion?.timestamp || conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (messageTimestamp) {
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            messageTimestamp,
            updatedFeatures
          );
        }

        closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        return;
      }

      // Use merged content if it's a specific section improvement
      const contentToUse = improveSection.mergedContent || improvedContent;

      // Update the feature with improved content
      const updatedFeatures = [...parsedFeatures].map(f => {
        if (f.id === improveSection.id) {
          // Remove duplicate title from improved content
          let cleanedContent = contentToUse;

          // Remove title if it appears at the beginning of improved content
          const titleText = f.title.replace(/^F\d+:\s*/, ''); // Remove feature ID prefix
          const patterns = [
            // "F001: Feature Name" at the beginning
            new RegExp(`^F\\d+:\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            // "Feature Name" at the beginning
            new RegExp(`^${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            // Bold versions
            new RegExp(`^\\*\\*F\\d+:\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            new RegExp(`^\\*\\*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            // With headers
            new RegExp(`^#+\\s*F\\d+:\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            new RegExp(`^#+\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i')
          ];

          for (const pattern of patterns) {
            cleanedContent = cleanedContent.replace(pattern, '').trim();
          }

          return {
            ...f,
            content: cleanedContent,
            rawContent: cleanedContent
          };
        }
        return f;
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedFeatures(updatedFeatures);

      // Reconstruct the full content with updated feature and category headers
      let updatedContent = '';
      let lastCategory = null;

      updatedFeatures.forEach((feat, idx) => {
        // Add category header if this is the first feature in a new category
        if (feat.isFirstInCategory && feat.category && feat.category !== lastCategory) {
          if (idx > 0) updatedContent += '\n';
          updatedContent += `# ${feat.category}\n\n`;
          lastCategory = feat.category;
        }

        // Add feature header
        updatedContent += `## ${feat.title}\n\n`;

        // Add feature content
        if (feat.content) {
          updatedContent += `${feat.content}\n\n`;
        }
      });

      featureContentRef.current = updatedContent.trim();

      // Update the current version with new features using the hook's update function
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          features: updatedFeatures,
          content: updatedContent.trim(),
          parsedContent: updatedFeatures // Some versions use parsedContent instead of features
        });
      }

      // Save to backend - update the specific message for the current version
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ Improve: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ Improve: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Also update the ParsedResponse so the artifact persists on refresh
      const messageTimestamp = currentVersion?.timestamp;
      if (messageTimestamp && conversation._id) {
        try {
          await conversationService.updateParsedSections(
            conversation._id,
            messageTimestamp,
            updatedFeatures
          );
          console.log('✅ Improve: ParsedResponse updated');
        } catch (err) {
          console.warn('ParsedResponse update failed:', err);
        }
      }

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ Improve: Called onConversationUpdate');
      }

      closeImprovementModal();

      showNotification({
        message: 'Feature improved successfully',
        type: 'success'
      });
    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };



  // Handler for adding new feature
  const handleAddNewFeature = async (newSectionData) => {
    try {
      // Create feature through backend API if available
      let dbFeature = null;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/projects/${projectId}/features`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            title: newSectionData.title,
            description: newSectionData.rawData?.description || newSectionData.content.split('\n')[0] || newSectionData.title,
            priority: newSectionData.rawData?.priority || 'should-have',
            complexity: newSectionData.rawData?.complexity || 'medium',
            userImpact: newSectionData.rawData?.userImpact || '',
            status: 'pending'
          })
        });

        if (response.ok) {
          const result = await response.json();
          dbFeature = result.data || result;
        }
      } catch (apiError) {
        console.log('Feature API not available, continuing with local storage only');
      }

      // Generate feature format for display
      // Since we renumber on delete, we can simply use length + 1
      const featureNumber = parsedFeatures.length + 1;
      const formattedFeatureId = `F${String(featureNumber).padStart(3, '0')}`;

      // Ensure content has proper structure
      let formattedContent = newSectionData.content;

      // If the content doesn't look properly formatted (no bold markers), it might be raw
      if (!formattedContent.includes('**') && newSectionData.rawData) {
        // Regenerate the content from rawData to ensure proper formatting
        const contentFields = analyzeContentFields(parsedFeatures);
        formattedContent = generateContentFromFields(newSectionData.rawData, contentFields);
      }

      const displayFeature = {
        id: formattedFeatureId,
        title: `${formattedFeatureId}: ${newSectionData.title}`,
        content: formattedContent,
        rawContent: formattedContent, // Store raw content for editing
        dbId: dbFeature?.id // Store database ID for reference if available
      };

      // Only add to the latest version
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new features',
          type: 'warning'
        });
        setShowAddFeatureModal(false);
        return;
      }

      // Add the new feature to the current features
      const updatedFeatures = [...parsedFeatures, displayFeature];

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedFeatures(updatedFeatures);

      // Expand the new feature by default
      setExpandedFeatures(prev => ({ ...prev, [displayFeature.id]: true }));

      // Ensure the new feature is visible if we're using pagination
      if (visibleFeatures < updatedFeatures.length) {
        setVisibleFeatures(updatedFeatures.length);
      }

      // Update conversation messages with new feature
      const featureText = `## ${displayFeature.title}\n\n${displayFeature.content}`;
      const updatedContent = featureContentRef.current + '\n\n' + featureText;

      // Update content ref
      featureContentRef.current = updatedContent;

      // Save to backend - update the specific message for the current version
      const updatedMessages = [...conversation.messages];

      // Use currentVersion.messageIndex to find the correct message to update
      // This ensures we update the right message when there are multiple versions
      const messageIndex = currentVersion?.messageIndex;
      let messageUpdated = false;

      if (messageIndex !== undefined && updatedMessages[messageIndex]) {
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          content: updatedContent.trim()
        };
        messageUpdated = true;
        console.log('Updated message at index:', messageIndex);
      }

      // Fallback: find the last assistant message if messageIndex is not available
      if (!messageUpdated) {
        console.log('Fallback: updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      console.log('🔵 handleAddNewFeature - Saving conversation:', {
        conversationId: conversation._id,
        currentVersionIndex,
        messageIndex: currentVersion?.messageIndex,
        totalMessages: updatedMessages.length,
        updatedMessageContentLength: updatedContent.trim().length
      });

      const saveResult = await conversationService.saveConversation(conversation._id, conversationData);
      console.log('🟢 Save result:', saveResult);

      // Persist to ParsedResponse so new section survives refresh
      const messageTimestamp = currentVersion?.timestamp;
      if (messageTimestamp && conversation._id) {
        try {
          await conversationService.updateParsedSections(
            conversation._id,
            messageTimestamp,
            updatedFeatures
          );
          console.log('✅ Add: ParsedResponse updated');
        } catch (err) {
          console.warn('ParsedResponse update on add failed:', err);
        }
      }

      showNotification({
        message: 'Feature added successfully',
        type: 'success'
      });

      // Close modal after successful save
      setShowAddFeatureModal(false);

      // Update the current version with the new features
      if (updateCurrentVersion && currentVersion) {
        const updatedVersion = {
          ...currentVersion,
          features: updatedFeatures,
          content: updatedContent.trim(),
          parsedContent: updatedFeatures
        };
        updateCurrentVersion(updatedVersion);
      }

      // Update the conversation in the parent component to reflect changes in left panel
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Scroll to the new feature
      setTimeout(() => {
        if (featurePanelRef.current) {
          featurePanelRef.current.scrollTop = featurePanelRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error adding new feature:', error);
      showNotification({
        message: `Failed to add new feature: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  };

  // Auto-scroll feature panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && featurePanelRef.current && isFeatureConversation) {
      // Wait a bit to ensure content renders before scrolling
      setTimeout(() => {
        if (featurePanelRef.current) {
          featurePanelRef.current.scrollTop = featurePanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isFeatureConversation]);

  // Check if all PRD content exists (features, user stories, user roles, process flows)
  useEffect(() => {
    const checkDownloadAvailability = async () => {
      if (!projectId) {
        setIsDownloadAvailable(false);
        return;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
        const response = await fetch(`${apiUrl}/download/features/${projectId}/availability`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Only show download button if ALL content types are available
          const available = data?.data?.all_available || false;
          setIsDownloadAvailable(available);
          console.log('Download availability:', data?.data);
        } else {
          setIsDownloadAvailable(false);
        }
      } catch (error) {
        console.error('Error checking download availability:', error);
        setIsDownloadAvailable(false);
      }
    };

    checkDownloadAvailability();
  }, [projectId]);

  // Handler for downloading a single feature with related content
  const handleDownloadFeature = async (feature) => {
    if (!projectId || !feature?.id) return;

    // Extract feature ID (F001, F002, etc.) from title or id
    // Title format is like "F001: User registration" or "F055: SMS Verification"
    // ID format can be "f001-user-registration" or "feature-f055-sms-verification"
    let featureId = feature.id;

    // Try to extract from title first (more reliable)
    const titleMatch = feature.title?.match(/(F\d+)/i);
    if (titleMatch) {
      featureId = titleMatch[1].toUpperCase(); // Ensure uppercase F001
    } else {
      // Fallback: extract from id - handles both "f001-..." and "feature-f055-..."
      const idMatch = feature.id?.match(/(f\d+)/i);
      if (idMatch) {
        featureId = idMatch[1].toUpperCase();
      }
    }

    setDownloadingFeatureId(feature.id);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
      const response = await fetch(`${apiUrl}/download/features/${projectId}/${featureId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to download feature');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${feature.id}_feature.md`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification({
        message: `"${feature.title || featureId}" downloaded successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error downloading feature:', error);
      showNotification({
        message: error.message || 'Failed to download feature',
        type: 'error'
      });
    } finally {
      setDownloadingFeatureId(null);
    }
  };

  // Don't show the split view if this isn't a feature conversation
  // or if no features are found and we're not streaming
  if (!isFeatureConversation || (!showFeaturePanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>

      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Feature accordion panel */}
        <Box
          ref={featurePanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Features' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      console.log('🔄 Version changed - reset isLocalUpdateRef');
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddFeatureModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportFeatures}
                  isExporting={isExporting}
                  onCopy={handleCopyFeatures}
                  onRefresh={handleRefreshFeatures}
                  disabled={!featureContentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add features" : "Add new feature"}
                  exportTooltip="Export features as markdown"
                  copyTooltip="Copy features to clipboard"
                  refreshTooltip="Refresh features"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'features'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedFeatures}
              totalItems={parsedFeatures?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Show parsed features or a placeholder if none found yet */}
            {/* Loading while generating */}
            {isStreaming ? (
              <LoadingBox message='Generating new features...' spinnerSize={40} />
            ) : showFeaturePanel && parsedFeatures.length > 0 ? (
              <Box sx={{
                p: 3,
                pt: 2,
                flex: 1,
                overflow: "auto",
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
              }}>
                {/* Render features as accordions using the hook-based approach */}
                {parsedFeatures.slice(0, visibleFeatures).map((feature, index) => {
                  const featureId = feature.id;
                  // Use version-specific ID for validation check
                  const versionSpecificId = `${featureId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                  const isValidated = validatedFeatures[versionSpecificId] || false;

                  return (
                    <React.Fragment key={featureId}>
                      {/* Display category header if this is the first feature in a category */}
                      {feature.isFirstInCategory && feature.category && (
                        <Box sx={{
                          mt: index === 0 ? 0 : 3,
                          mb: 2,
                          px: 2
                        }}>
                          <SectionHeader label={feature.category} />
                        </Box>
                      )}
                      <Accordion
                        ref={(el) => {
                          registerSectionRef(featureId, el);
                          if (shouldHighlight(featureId)) highlightRef.current = el;
                        }}
                        title={feature.title}
                        defaultExpanded={false}
                        expanded={expandedFeatures[featureId]}
                        onChange={(_, expanded) => handleFeatureToggle(featureId)}
                        titleContent={editingFeature === featureId ? (
                          <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                            <TextField
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.target.removeAttribute('title')}
                              size="small"
                              fullWidth
                              placeholder="Enter feature title"
                              variant="outlined"
                              InputProps={{ title: '' }}
                              inputProps={{ title: '', autoComplete: 'off' }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  backgroundColor: '#fff',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                  },
                                  '&:hover fieldset': {
                                    borderWidth: '1px',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderWidth: '1px',
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  padding: '8px 12px',
                                },
                              }}
                            />
                          </Box>
                        ) : null}
                        rightIcon={
                          <Box
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await handleValidateFeature(featureId);
                                // Notification is handled by useValidation hook
                              } catch (error) {
                                showNotification({
                                  message: `Failed to update validation for "${feature.title}"`,
                                  type: "error"
                                });
                              }
                            }}
                            sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                          >
                            {isValidated ? (
                              <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                            ) : (
                              <UnvalidatedIcon size={25} />
                            )}
                          </Box>
                        }
                      >
                        {/* Feature content with proper formatting */}
                        <ScrollableBox
                          sx={{
                            textAlign: 'justify',
                            cursor: editingFeature === featureId ? 'default' : 'pointer',
                          }}
                        >
                          {editingFeature === featureId ? (
                            // Edit mode
                            <EditableTextField
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                            />
                          ) : (
                            // Normal content display — use structured details if available, else markdown
                            <Box
                              ref={(el) => { getSectionRef(featureId).current = el; }}
                              sx={{ position: 'relative' }}
                            >
                              <SectionDetailsRenderer
                                section={feature}
                                taskId={conversation?.taskId || conversation?.task_id}
                                projectId={projectId}
                              />
                              {/* Inline improve input */}
                              {showImproveInput === featureId && (
                                <InlineImproveInput
                                  improvePrompt={improvePrompt}
                                  onPromptChange={setImprovePrompt}
                                  onSend={() => handleSendImprove(feature)}
                                  onCancel={handleCancelImprove}
                                  promptText="How would you like to improve this feature?"
                                  placeholder="Describe the improvements you'd like..."
                                />
                              )}
                              {/* Text selection comment popup */}
                              <TextSelectionPopup
                                containerRef={getSectionRef(featureId)}
                                sectionId={featureId}
                                onSubmitComment={addComment}
                                disabled={editingFeature === featureId}
                              />
                            </Box>
                          )}
                        </ScrollableBox>

                      {/* Action buttons */}
                      <Box sx={{
                        position: 'absolute',
                        right: 10,
                        top: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5
                      }}>
                        <ActionButtons
                          item={feature}
                          itemIndex={index}
                          featureId={featureId}
                          editingFeature={editingFeature}
                          isSaving={isSaving}
                          onEdit={() => handleStartEdit(featureId, feature)}
                          onSave={() => handleSaveEdit(featureId, index)}
                          onCancel={handleCancelEdit}
                          onImprove={() => handleImproveClick(feature)}
                          onExplain={() => handleExplainFeature(feature)}
                          onCopy={() => handleCopyFeature(feature)}
                          onDownload={isDownloadAvailable ? () => handleDownloadFeature(feature) : null}
                          isDownloading={downloadingFeatureId === featureId}
                          onDelete={() => handleDeleteFeatureClick(feature)}
                          vertical={true}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                          onComment={() => openCommentPanel(featureId)}
                          commentCount={commentCounts[featureId]?.open || 0}
                        />
                      </Box>
                    </Accordion>
                    </React.Fragment>
                  );
                })}

                {/* Load more button for lazy loading */}
                {parsedFeatures.length > visibleFeatures && (
                  <LoadMoreButton
                    remaining={parsedFeatures.length - visibleFeatures}
                    onLoadMore={handleLoadMore}
                    onShowAll={handleShowAll}
                    label="Load more"
                  />
                )}

              </Box>
            ) : (
              <EmptyStateBox message="No features found in this conversation yet." />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete feature"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirm}
      />



      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddFeatureModal}
        onClose={() => setShowAddFeatureModal(false)}
        onSave={handleAddNewFeature}
        sectionType="feature"
        existingSections={parsedFeatures}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          parsedFeatures.find(f => f.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />

    </Box>
  );
};

export default SplitViewConversation;